import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  BookOpen, Search, ChevronLeft, ChevronRight, 
  Loader2, Bookmark, BookmarkCheck, Share2, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BibleReader() {
  const { user } = useAuth();
  const [books, setBooks] = useState([]);
  const [versions, setVersions] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(1);
  const [selectedVersion, setSelectedVersion] = useState(user?.bible_version || 'web');
  const [chapterContent, setChapterContent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingChapter, setLoadingChapter] = useState(false);
  const [view, setView] = useState('books'); // books, chapters, reading, search

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (user?.bible_version) {
      setSelectedVersion(user.bible_version);
    }
  }, [user]);

  const fetchInitialData = async () => {
    try {
      const [booksRes, versionsRes, bookmarksRes] = await Promise.all([
        axios.get(`${API}/bible/books`),
        axios.get(`${API}/bible/versions`),
        axios.get(`${API}/bookmarks`)
      ]);
      setBooks(booksRes.data.books);
      setVersions(versionsRes.data.versions);
      setBookmarks(bookmarksRes.data);
    } catch (error) {
      toast.error('Could not load Bible data');
    } finally {
      setLoading(false);
    }
  };

  const selectBook = (book) => {
    setSelectedBook(book);
    setSelectedChapter(1);
    setView('chapters');
  };

  const loadChapter = async (chapter) => {
    setSelectedChapter(chapter);
    setLoadingChapter(true);
    
    try {
      const response = await axios.get(
        `${API}/bible/chapter/${selectedBook.name}/${chapter}?translation=${selectedVersion}`
      );
      setChapterContent(response.data);
      setView('reading');
    } catch (error) {
      toast.error('Could not load chapter');
    } finally {
      setLoadingChapter(false);
    }
  };

  const navigateChapter = async (direction) => {
    const newChapter = selectedChapter + direction;
    if (newChapter >= 1 && newChapter <= selectedBook.chapters) {
      await loadChapter(newChapter);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 3) {
      toast.error('Please enter at least 3 characters');
      return;
    }

    setLoadingChapter(true);
    try {
      const response = await axios.get(
        `${API}/bible/search?q=${encodeURIComponent(searchQuery)}&translation=${selectedVersion}`
      );
      setSearchResults(response.data.results);
      setView('search');
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoadingChapter(false);
    }
  };

  const isBookmarked = (content) => {
    return bookmarks.some(b => b.content === content);
  };

  const toggleBookmark = async (verse, reference) => {
    const existing = bookmarks.find(b => b.content === verse);
    
    if (existing) {
      try {
        await axios.delete(`${API}/bookmarks/${existing.id}`);
        setBookmarks(prev => prev.filter(b => b.id !== existing.id));
        toast.success('Removed from bookmarks');
      } catch (error) {
        toast.error('Could not remove bookmark');
      }
    } else {
      try {
        const response = await axios.post(`${API}/bookmarks`, {
          type: 'verse',
          content: verse,
          reference: reference
        });
        setBookmarks(prev => [response.data, ...prev]);
        toast.success('Added to bookmarks');
      } catch (error) {
        toast.error('Could not bookmark');
      }
    }
  };

  const shareVerse = async (verse, reference) => {
    const shareText = `"${verse}"\n— ${reference}\n\nShared from BlessBe`;
    
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText });
      } catch (error) {
        if (error.name !== 'AbortError') {
          await navigator.clipboard.writeText(shareText);
          toast.success('Verse copied!');
        }
      }
    } else {
      await navigator.clipboard.writeText(shareText);
      toast.success('Verse copied!');
    }
  };

  const renderBooksView = () => (
    <div className="space-y-4 pb-24 animate-fade-in" data-testid="books-view">
      {/* Old Testament */}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Old Testament</h3>
        <div className="grid grid-cols-2 gap-2">
          {books.slice(0, 39).map((book) => (
            <Button
              key={book.abbrev}
              variant="outline"
              className="justify-start h-auto py-3 px-4 rounded-xl border-gray-200 hover:border-blue-300 hover:bg-blue-50"
              onClick={() => selectBook(book)}
              data-testid={`book-${book.abbrev}`}
            >
              <span className="text-sm font-medium truncate">{book.name}</span>
            </Button>
          ))}
        </div>
      </div>
      
      {/* New Testament */}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">New Testament</h3>
        <div className="grid grid-cols-2 gap-2">
          {books.slice(39).map((book) => (
            <Button
              key={book.abbrev}
              variant="outline"
              className="justify-start h-auto py-3 px-4 rounded-xl border-gray-200 hover:border-blue-300 hover:bg-blue-50"
              onClick={() => selectBook(book)}
              data-testid={`book-${book.abbrev}`}
            >
              <span className="text-sm font-medium truncate">{book.name}</span>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderChaptersView = () => (
    <div className="space-y-4 pb-24 animate-fade-in" data-testid="chapters-view">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setView('books')}
        className="rounded-full"
      >
        <ChevronLeft className="w-4 h-4 mr-1" />
        Back to Books
      </Button>
      
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">{selectedBook?.name}</h2>
        <p className="text-sm text-gray-500">Select a chapter</p>
      </div>
      
      <div className="grid grid-cols-5 gap-2">
        {Array.from({ length: selectedBook?.chapters || 0 }, (_, i) => i + 1).map((chapter) => (
          <Button
            key={chapter}
            variant="outline"
            className="h-12 rounded-xl border-gray-200 hover:border-blue-300 hover:bg-blue-50 font-medium"
            onClick={() => loadChapter(chapter)}
            data-testid={`chapter-${chapter}`}
          >
            {chapter}
          </Button>
        ))}
      </div>
    </div>
  );

  const renderReadingView = () => (
    <div className="space-y-4 pb-24 animate-fade-in" data-testid="reading-view">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setView('chapters')}
          className="rounded-full"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Chapters
        </Button>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            disabled={selectedChapter <= 1}
            onClick={() => navigateChapter(-1)}
            className="rounded-full"
            data-testid="prev-chapter"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <span className="text-sm font-medium text-gray-600">
            Ch. {selectedChapter}
          </span>
          <Button
            variant="ghost"
            size="icon"
            disabled={selectedChapter >= selectedBook?.chapters}
            onClick={() => navigateChapter(1)}
            className="rounded-full"
            data-testid="next-chapter"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>
      
      <Card className="rounded-2xl border-0 shadow-card bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-800">{chapterContent?.reference}</h2>
              <p className="text-xs text-gray-500">{chapterContent?.translation}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => shareVerse(chapterContent?.text?.substring(0, 200) + '...', chapterContent?.reference)}
              className="rounded-full"
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
          
          {chapterContent?.verses?.length > 0 ? (
            <div className="space-y-3">
              {chapterContent.verses.map((verse, idx) => (
                <div key={idx} className="group">
                  <span className="text-xs font-bold text-blue-500 mr-2">{verse.verse}</span>
                  <span className="text-gray-700 leading-relaxed">{verse.text}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {chapterContent?.text}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderSearchView = () => (
    <div className="space-y-4 pb-24 animate-fade-in" data-testid="search-view">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setView('books');
          setSearchResults(null);
          setSearchQuery('');
        }}
        className="rounded-full"
      >
        <X className="w-4 h-4 mr-1" />
        Clear Search
      </Button>
      
      <p className="text-sm text-gray-500">
        {searchResults?.length || 0} results for "{searchQuery}"
      </p>
      
      {searchResults?.length === 0 ? (
        <div className="text-center py-8">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No verses found</p>
          <p className="text-sm text-gray-400">Try different keywords</p>
        </div>
      ) : (
        <div className="space-y-3">
          {searchResults?.map((result, idx) => (
            <Card key={idx} className="rounded-2xl border-0 shadow-card bg-white">
              <CardContent className="p-4">
                <p className="text-gray-700 leading-relaxed mb-2 italic">
                  "{result.verse}"
                </p>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-blue-600 font-semibold">{result.reference}</p>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => toggleBookmark(result.verse, result.reference)}
                    >
                      {isBookmarked(result.verse) ? (
                        <BookmarkCheck className="w-4 h-4 text-blue-500" />
                      ) : (
                        <Bookmark className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => shareVerse(result.verse, result.reference)}
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen sky-gradient flex items-center justify-center">
        <div className="text-center">
          <img src="/images/logo.png" alt="BlessBe" className="w-20 h-20 mx-auto mb-4 rounded-full animate-pulse" />
          <p className="text-gray-600">Loading Bible...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen sky-gradient-light pb-safe" data-testid="bible-reader-page">
      {/* Header */}
      <header className="px-6 pt-8 pb-4">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full overflow-hidden">
            <img src="/images/logo.png" alt="BlessBe" className="w-10 h-10 object-cover" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">The Bible</h1>
            <select
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(e.target.value)}
              className="text-xs text-blue-600 bg-transparent border-none cursor-pointer font-medium"
              data-testid="version-select"
            >
              {versions.map(v => (
                <option key={v.id} value={v.id}>{v.abbrev} - {v.name}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search verses (e.g., love, peace, faith)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-10 pr-16 h-12 rounded-full bg-white border-gray-200 shadow-sm"
            data-testid="search-input"
          />
          {searchQuery.length >= 3 && (
            <Button
              size="sm"
              onClick={handleSearch}
              disabled={loadingChapter}
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full h-10 px-4"
            >
              {loadingChapter ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
            </Button>
          )}
        </div>
      </header>

      {/* Content */}
      <ScrollArea className="flex-1 px-6">
        {loadingChapter && view !== 'search' ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <>
            {view === 'books' && renderBooksView()}
            {view === 'chapters' && renderChaptersView()}
            {view === 'reading' && renderReadingView()}
            {view === 'search' && renderSearchView()}
          </>
        )}
      </ScrollArea>

      <BottomNav />
    </div>
  );
}
